import { Injectable, OnDestroy, inject } from '@angular/core';

import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  IdTokenClaims,
  SsoSilentRequest,
} from '@azure/msal-browser';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  filter,
  from,
  map,
  of,
  switchMap,
  takeUntil,
  throwError,
} from 'rxjs';

import { CamTenantService, MappingApiType } from '@camelot/server';
import { CamAuthService, CamUserService, UserProfile } from '@camelot/user';
import { trigram } from '@camelot/utils';

import { AppRedirectService } from 'src/app/services/redirect.service';
import { environment } from 'src/environments/environment';

const apiRoutes: MappingApiType = {
  GetUserProfile: {
    type: 'GET',
    url: '{ApiUrl}/UserProfile',
  },
};

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string;
  tfp?: string;
};

@Injectable({
  providedIn: 'root',
})
export class AzureAuthService extends CamAuthService<any> implements OnDestroy {
  private readonly _destroying$ = new Subject<void>();

  private authService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private redirectService = inject(AppRedirectService);
  private _tenantService = inject(CamTenantService);

  override get userProfile$(): BehaviorSubject<UserProfile | null> {
    return this.userService.userProfile$;
  }

  override get trigram(): string | null | undefined {
    return trigram(this.userProfile$.value?.trigram);
  }

  override get firstLetter(): string | null {
    const name = this.userProfile$.value?.trigram;
    if (!name) {
      return '-';
    }
    return name[0].toUpperCase();
  }

  public userService = inject(CamUserService);
  constructor() {
    super(apiRoutes);

    this.user$.pipe(takeUntil(this._destroying$)).subscribe(userProfile => {
      const currentUrl = window.location.href;
      const url = new URL(currentUrl);
      const urlPath = url.pathname;
      if (userProfile || urlPath.includes('login') || urlPath === environment.AADB2C.redirectUri) {
        return;
      }
      this.redirectService.setUrlPath(urlPath + url.search);
    });

    this.authService
      .initialize()
      .pipe(
        switchMap(() => {
          return this.acquireTokenSilently([]);
        }),
        map(result => {
          this.user$.next(result.account);
          this._permissionsService.setAuthenticated(true);
          this._serverService.isAuthenticated = true;
        }),
        catchError(err => {
          this._permissionsService.setAuthenticated(false);
          this._serverService.isAuthenticated = false;
          this._tenantService.setConfigTenant();
          return of();
        })
      )
      .subscribe();

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.SSO_SILENT_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        let payload = result.payload as AuthenticationResult;
        let idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

        if (
          idtoken.acr === environment.AADB2C.b2cPolicies.names.signUpSignIn ||
          idtoken.tfp === environment.AADB2C.b2cPolicies.names.signUpSignIn
        ) {
          this.authService.instance.setActiveAccount(payload.account);
        }

        // The B2C policy is in the `acr` claim, or `tfp` on newer policies.
        if (
          idtoken.acr === environment.AADB2C.b2cPolicies.names.editProfile ||
          idtoken.tfp === environment.AADB2C.b2cPolicies.names.editProfile
        ) {
          const originalSignInAccount = this.authService.instance
            .getAllAccounts()
            .find(
              (account: AccountInfo) =>
                account.idTokenClaims?.oid === idtoken.oid &&
                account.idTokenClaims?.sub === idtoken.sub &&
                ((account.idTokenClaims as IdTokenClaimsWithPolicyId).acr ===
                  environment.AADB2C.b2cPolicies.names.signUpSignIn ||
                  (account.idTokenClaims as IdTokenClaimsWithPolicyId).tfp ===
                    environment.AADB2C.b2cPolicies.names.signUpSignIn)
            );

          let signUpSignInFlowRequest: SsoSilentRequest = {
            authority: environment.AADB2C.b2cPolicies.authorities.signUpSignIn.authority,
            account: originalSignInAccount,
          };

          this.authService.ssoSilent(signUpSignInFlowRequest);
        }

        // Returning from the reset-password flow: force a reauthentication.
        if (
          idtoken.acr === environment.AADB2C.b2cPolicies.names.resetPassword ||
          idtoken.tfp === environment.AADB2C.b2cPolicies.names.resetPassword
        ) {
          this.login();
        }

        return result;
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        // AADB2C90118 = the user asked to reset their password.
        if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
          this.login();
        }
      });
  }

  override ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  override fetchUserProfile(): Observable<UserProfile> {
    return this.userService.fetchUserProfile();
  }

  override load(): void {}

  async login(targetUrl?: string) {
    await this.authService.instance.initialize();
    await this.authService.instance.handleRedirectPromise();
    const target = targetUrl ?? this.redirectService.getUrlPath() ?? undefined;
    this.redirectService.setUrlPath(null);
    this.authService.loginRedirect(target ? { redirectStartPage: target, scopes: [] } : undefined);
  }

  async logout() {
    return this.authService.instance
      .handleRedirectPromise()
      .then(authResult => {
        const account = this.authService.instance.getActiveAccount();
        this.authService.instance
          .logoutRedirect({
            account: account,
            postLogoutRedirectUri: '/',
          })
          .then<null>(result => {
            this._permissionsService.setAuthenticated(false);
            this._serverService.isAuthenticated = false;
            return null;
          });
      })
      .catch(err => {
        console.log(err);
      })
      .then<null>(result => {
        this._permissionsService.setAuthenticated(false);
        this._serverService.isAuthenticated = false;
        return null;
      });
  }

  getTokenFromCache() {
    return this.authService.initialize().pipe(
      switchMap(() => {
        return this.acquireTokenSilently([]);
      })
    );
  }

  acquireTokenSilently(scopes: string[]): Observable<AuthenticationResult> {
    const account = this.authService.instance.getAllAccounts()[0];

    return from(
      this.authService.acquireTokenSilent({
        account: account,
        scopes: scopes,
      })
    ).pipe(
      catchError(error => {
        return this.acquireTokenInteractive(scopes);
      })
    );
  }

  acquireTokenInteractive(scopes: string[]): Observable<AuthenticationResult> {
    return from(this.authService.instance.handleRedirectPromise()).pipe(
      switchMap(authResult => {
        if (authResult) {
          return of(authResult);
        } else {
          return throwError(() => new Error('Interactive login failed'));
        }
      })
    );
  }
}
